import React, { useState, useEffect } from 'react';
import bookingService from "../../../../../../services/booking.service";
import { useEyeconxStore } from "../../../../../../store/eyeconxStore";
import Loading from "../../../../../common/Loading";

const DoctorAppointmentTypes = ({ doctor }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [allAppointmentTypes, setAllAppointmentTypes] = useState([]);
    const [doctorAppointmentTypes, setDoctorAppointmentTypes] = useState([]);
    const [selectedTypesToAdd, setSelectedTypesToAdd] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (doctor?.id) {
            getDoctorAppointmentTypes();
            getAllAppointmentTypes();
        }
    }, [doctor]);

    const getAllAppointmentTypes = async () => {
        try {
            const res = await bookingService.getAllAppointmentTypes(clinicId);
            setAllAppointmentTypes(res.data);
        } catch (error) {
            console.log('Error fetching all appointment types:', error);
        }
    };

    const getDoctorAppointmentTypes = async () => {
        try {
            setIsLoading(true);
            const res = await bookingService.getDoctorAppointmentTypes(clinicId, doctor.id);
            const doctorTypes = res.data || [];
            setDoctorAppointmentTypes(doctorTypes);
            console.log('Doctor appointment types:', doctorTypes);
        } catch (error) {
            console.log('Error fetching doctor appointment types:', error);
            setDoctorAppointmentTypes([]);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteAppointmentType = async (apptTypeId) => {
        try {
            setIsDeleting(true);
            const data = [apptTypeId];
            await bookingService.deleteDoctorAppointmentTypes(clinicId, doctor.id, data);
            await getDoctorAppointmentTypes(); // Refresh the list
        } catch(error) {
            console.log('Error deleting appointment type:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const addSelectedAppointmentTypes = async () => {
        try {
            setIsSaving(true);
            if (selectedTypesToAdd.length > 0) {
                await bookingService.addUpdateDoctorAppointmentTypes(clinicId, doctor.id, selectedTypesToAdd);
                setSelectedTypesToAdd([]); // Clear selection
                await getDoctorAppointmentTypes(); // Refresh the list
            }
        } catch (error) {
            console.log('Error adding appointment types:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCheckboxChange = (appointmentTypeId) => {
        setSelectedTypesToAdd(prev => {
            if (prev.includes(appointmentTypeId)) {
                return prev.filter(id => id !== appointmentTypeId);
            } else {
                return [...prev, appointmentTypeId];
            }
        });
    };

    const handleSelectAllAvailable = () => {
        const availableTypes = getAvailableAppointmentTypes();
        if (selectedTypesToAdd.length === availableTypes.length) {
            setSelectedTypesToAdd([]);
        } else {
            setSelectedTypesToAdd(availableTypes.map(type => type.id));
        }
    };

    const handleCancel = () => {
        setSelectedTypesToAdd([]);
        setIsEditing(false);
    };

    const getAppointmentTypeColor = (timeSlot) => {
        switch (timeSlot) {
            case 15: return 'bg-info';
            case 20: return 'bg-primary';
            case 30: return 'bg-success';
            case 45: return 'bg-warning';
            case 60: return 'bg-danger';
            default: return 'bg-secondary';
        }
    };

    // Get available appointment types that are not assigned to the doctor
    const getAvailableAppointmentTypes = () => {
        const doctorTypeIds = doctorAppointmentTypes.map(type => type.appointmentTypeId);
        return allAppointmentTypes.filter(type => !doctorTypeIds.includes(type.id));
    };

    if (isLoading) {
        return <Loading />;
    }

    const availableAppointmentTypes = getAvailableAppointmentTypes();

    return (
        <div className="doctor-appointment-types mt-3">
            {/* Display Mode - Badges */}
            {!isEditing && (
                <div className="position-relative border rounded p-3">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        {doctorAppointmentTypes.length > 0 ? (
                            doctorAppointmentTypes.map((type) => (
                                <span
                                    key={type.id}
                                    className={`badge ${getAppointmentTypeColor(type.timeSlot)} text-white d-flex align-items-center`}
                                >
                                    {type.apptType}
                                    <small className="ms-2 opacity-75">
                                        ({type.timeSlot}min)
                                    </small>
                                </span>
                            ))
                        ) : (
                            <span className="text-muted fst-italic">
                                No appointment types assigned
                            </span>
                        )}
                    </div>

                    {/* Edit button at top-right */}
                    <button
                        className="btn btn-sm btn-outline-primary position-absolute top-0 end-0 m-2"
                        onClick={() => setIsEditing(true)}
                    >
                        <i className="bi bi-pencil"></i>
                    </button>
                </div>
            )}

            {/* Edit Mode */}
            {isEditing && (
                <div className="card">
                    <div className="card-body">
                        <h6 className="card-title mb-4">
                            <i className="bi bi-calendar-check me-2"></i>
                            Manage Appointment Types
                        </h6>

                        {/* Current Appointment Types Section */}
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0 text-primary">Current Appointment Types</h6>
                                <small className="text-muted">
                                    {doctorAppointmentTypes.length} assigned
                                </small>
                            </div>

                            {doctorAppointmentTypes.length > 0 ? (
                                <div className="border rounded">
                                    {doctorAppointmentTypes.map((type) => (
                                        <div key={type.id} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                                            <div className="d-flex align-items-center">
                                                <span className={`badge ${getAppointmentTypeColor(type.timeSlot)} text-white me-3`}>
                                                    {type.timeSlot}min
                                                </span>
                                                <div>
                                                    <div className="fw-medium">{type.apptType}</div>
                                                    <small className={`badge ${type.isActive ? 'bg-success' : 'bg-secondary'} text-white`}>
                                                        {type.isActive ? 'Active' : 'Inactive'}
                                                    </small>
                                                </div>
                                            </div>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => deleteAppointmentType(type.appointmentTypeId)}
                                                disabled={isDeleting}
                                                title="Remove appointment type"
                                            >
                                                {isDeleting ? (
                                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                                ) : (
                                                    <i className="bi bi-trash"></i>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 border rounded bg-light">
                                    <i className="bi bi-calendar-x display-6 text-muted mb-2"></i>
                                    <p className="text-muted mb-0">No appointment types assigned</p>
                                </div>
                            )}
                        </div>

                        {/* Available Appointment Types Section */}
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0 text-success">Available Appointment Types</h6>
                                <div className="btn-group">
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={handleSelectAllAvailable}
                                        disabled={availableAppointmentTypes.length === 0}
                                    >
                                        <i className="bi bi-check-all me-1"></i>
                                        {selectedTypesToAdd.length === availableAppointmentTypes.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                            </div>

                            {availableAppointmentTypes.length > 0 ? (
                                <div className="border rounded">
                                    {availableAppointmentTypes.map((type) => (
                                        <div key={type.id} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                                            <div className="form-check flex-grow-1">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`available-type-${type.id}`}
                                                    checked={selectedTypesToAdd.includes(type.id)}
                                                    onChange={() => handleCheckboxChange(type.id)}
                                                />
                                                <label
                                                    className="form-check-label d-flex align-items-center w-100"
                                                    htmlFor={`available-type-${type.id}`}
                                                >
                                                    <span className={`badge ${getAppointmentTypeColor(type.timeSlot)} text-white me-3`}>
                                                        {type.timeSlot}min
                                                    </span>
                                                    <div>
                                                        <div className="fw-medium">{type.apptType}</div>
                                                        <small className={`badge ${type.isActive ? 'bg-success' : 'bg-secondary'} text-white`}>
                                                            {type.isActive ? 'Active' : 'Inactive'}
                                                        </small>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 border rounded bg-light">
                                    <i className="bi bi-check-circle display-6 text-muted mb-2"></i>
                                    <p className="text-muted mb-0">All appointment types are assigned</p>
                                </div>
                            )}

                            {/* Add Selected Button */}
                            {selectedTypesToAdd.length > 0 && (
                                <div className="mt-3">
                                    <button
                                        className="btn btn-success w-100"
                                        onClick={addSelectedAppointmentTypes}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Adding...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add Selected ({selectedTypesToAdd.length}) Appointment Types
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Summary */}
                        <div className="mt-3 p-3 bg-light rounded">
                            <small className="text-muted">
                                <i className="bi bi-info-circle me-1"></i>
                                <strong>{doctorAppointmentTypes.length}</strong> current appointment types •
                                <strong> {availableAppointmentTypes.length}</strong> available types •
                                <strong> {selectedTypesToAdd.length}</strong> selected to add
                            </small>
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleCancel}
                                disabled={isSaving || isDeleting}
                            >
                                <i className="bi bi-x me-1"></i>
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoctorAppointmentTypes;